import { useCallback, useEffect, useRef, useState } from 'react';

import type { InspoImportEntrySource } from '@/contexts/InspoImportContext';
import { useAuth } from '@/contexts';
import { useAuthAction } from '@/hooks/useAuthAction';
import { WebAnalytics } from '@/lib/analytics';
import {
  EVENT_INSPO_IMPORT_URL_VALIDATION_FAILED,
  EVENT_INSPO_LINK_SUBMITTED,
} from '@/lib/analyticsEvents';
import { detectSocialPlatform } from '@/utils/platformDetection';
import { resolveInspoImportEntryChannel } from '@/utils/inspoImportEntryChannel';
import { validateSocialMediaUrl } from '@/utils/urlValidation';

export interface UseSocialImportUrlSubmitOptions {
  entrySource?: InspoImportEntrySource;
  isOnboardingEmbed?: boolean;
  onUrlAccepted: (normalizedUrl: string) => void;
}

export function useSocialImportUrlSubmit({
  entrySource = 'in_app',
  isOnboardingEmbed = false,
  onUrlAccepted,
}: UseSocialImportUrlSubmitOptions) {
  const { user } = useAuth();
  const { requireAuth, showLoginModal, setShowLoginModal, loginModalRedirectPath } =
    useAuthAction();
  const [pendingUrlAfterLogin, setPendingUrlAfterLogin] = useState<string | null>(null);
  const [urlValidationError, setUrlValidationError] = useState('');
  const usedManualPasteSubmitRef = useRef(false);

  const inspoEntryChannelProps = useCallback(
    () => ({
      entry_channel: resolveInspoImportEntryChannel(entrySource, usedManualPasteSubmitRef.current),
    }),
    [entrySource],
  );

  const submitUrl = useCallback(
    (rawUrl: string, source: 'auto' | 'manual') => {
      const trimmed = rawUrl.trim();
      const validation = validateSocialMediaUrl(trimmed);
      if (!validation.isValid) {
        setUrlValidationError(validation.error || 'Paste a valid Instagram or TikTok link.');
        WebAnalytics.track(EVENT_INSPO_IMPORT_URL_VALIDATION_FAILED, {
          reason: validation.reason || 'invalid_url',
          source,
          ...inspoEntryChannelProps(),
        });
        return false;
      }
      const importUrl = validation.normalizedUrl ?? trimmed;
      if (source === 'manual') {
        usedManualPasteSubmitRef.current = true;
      }
      WebAnalytics.track(EVENT_INSPO_LINK_SUBMITTED, {
        link_url: importUrl,
        platform: detectSocialPlatform(importUrl),
        source,
        ...inspoEntryChannelProps(),
      });

      setPendingUrlAfterLogin(importUrl);
      requireAuth(
        'post',
        () => {
          setPendingUrlAfterLogin(null);
          onUrlAccepted(importUrl);
        },
        {
          title: 'Sign up to import from social',
          description:
            'Create an account to turn Instagram and TikTok posts into trips with notes and day structure.',
        },
      );
      return true;
    },
    [inspoEntryChannelProps, onUrlAccepted, requireAuth],
  );

  useEffect(() => {
    if (!user || !pendingUrlAfterLogin) return;
    const url = pendingUrlAfterLogin;
    setPendingUrlAfterLogin(null);
    onUrlAccepted(url);
  }, [onUrlAccepted, pendingUrlAfterLogin, user]);

  return {
    urlValidationError,
    setUrlValidationError,
    submitUrl,
    showLoginModal: showLoginModal && !isOnboardingEmbed,
    setShowLoginModal,
    loginModalRedirectPath,
  };
}
